import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/src/components/ui/input";
import { Button } from "@/src/components/ui/button";
import { Label } from "@/src/components/ui/label";
import { Textarea } from "@/src/components/ui/textarea";
import { Town } from "../model";
import { useFetch } from "@/src/lib/useFetch";
import { useToast } from "@/src/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { PostLocationSchema, PostLocationValues } from "../schemas";
import { RelationSingleInput } from "../components/RelationSingleInput";
import { FormCheckbox } from "@/src/components/ui/form-checkbox";
import { FormErrors } from "../components/FormErrors";

export function AddLocation() {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();

  const onSubmit = async (data: PostLocationValues) => {
    try {
      const response = await fetch<{ data: { id: string } }>("/api/locations/locations", "POST", data);
      toast({ title: "Success", description: "Location added successfully" });
      navigate(`/locations/${response.data.id}`);
    } catch (error) {
      toast({ title: "Error", description: "Failed to add location", variant: "destructive" });
    }
  };
  return (
    <div className="rounded-2xl shadow-md bg-white p-4">
      <h2 className="text-lg font-semibold">Edit location</h2>
      <EditLocationForm onSubmit={onSubmit} />
    </div>
  );
}

export function EditLocationForm({ onSubmit }: { onSubmit: (data: PostLocationValues) => void }) {
  const { fetch } = useFetch();

  const loadTowns = async (q: string) => {
    const data = await fetch<{ data: Town[] }>(
      `/api/locations/towns?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };
  const form = useForm<PostLocationValues>({
    resolver: zodResolver(PostLocationSchema),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit, console.error)} className="space-y-4 p-4">
        <div>
          <Label>Names</Label>
          <Textarea {...register("names")} placeholder="Enter names, one per line" />
        </div>

        <div>
          <Label>Latitude / Longitude</Label>
          <div className="flex space-x-2">
            <Input {...register("latitude", { valueAsNumber: true })} placeholder="Latitude" />
            <Input {...register("longitude", { valueAsNumber: true })} placeholder="Longitude" />
          </div>
        </div>

        <div>
          <Label>Address</Label>
          <Input {...register("address")} placeholder="Address" />
        </div>

        <div>
          <Label>Notes</Label>
          <Textarea {...register("notes")} placeholder="Notes" />
        </div>

        <RelationSingleInput name="town" label="Town" loadOptions={loadTowns} />

        <FormErrors errors={errors} />

        <Button type="submit">Save</Button>
      </form>
    </FormProvider>
  );
}
